/**
 * AlertsBanner Component
 * Displays notification badge and dropdown list of alerts
 *
 * Features:
 * - Badge with unread count
 * - Dropdown list of alerts
 * - Icons by severity
 * - Action buttons per alert
 * - Polling every 60s for new alerts
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Modal,
  ScrollView,
  Animated,
  Platform,
} from 'react-native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { useNavigation } from '@react-navigation/native';
import { colors } from '../../theme/colors';
import { typography } from '../../theme/typography';
import { spacing } from '../../theme/spacing';
import logger from '../../utils/logger';
import { alertsApi, Alert } from '../../api/endpoints/alerts';

interface AlertsBannerProps {
  onAlertPress?: (alert: Alert) => void;
}

const POLLING_INTERVAL = 60000; // 60 seconds

export default function AlertsBanner({ onAlertPress }: AlertsBannerProps) {
  const navigation = useNavigation();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const pulseAnim = useRef(new Animated.Value(1)).current;

  // Fetch alerts
  const fetchAlerts = useCallback(async () => {
    try {
      const response = await alertsApi.getAlerts({ limit: 20 });
      if (response.success) {
        setAlerts(response.alerts);
        setUnreadCount(response.unread_count);

        // Trigger pulse animation if new unread alerts
        if (response.unread_count > 0) {
          startPulseAnimation();
        }
      }
    } catch (error) {
      logger.error('Error fetching alerts:', error);
    }
  }, []);

  // Fetch just the count (lighter request for polling)
  const fetchCount = useCallback(async () => {
    try {
      const response = await alertsApi.getCount();
      if (response.success) {
        const hadUnread = unreadCount > 0;
        setUnreadCount(response.unread_count);

        // Animate if we got new alerts
        if (!hadUnread && response.unread_count > 0) {
          startPulseAnimation();
        }
      }
    } catch (error) {
      // Silent fail for polling
    }
  }, [unreadCount]);

  // Pulse animation for new alerts
  const startPulseAnimation = () => {
    Animated.sequence([
      Animated.timing(pulseAnim, {
        toValue: 1.2,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(pulseAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(pulseAnim, {
        toValue: 1.2,
        duration: 200,
        useNativeDriver: true,
      }),
      Animated.timing(pulseAnim, {
        toValue: 1,
        duration: 200,
        useNativeDriver: true,
      }),
    ]).start();
  };

  // Initial fetch and polling
  useEffect(() => {
    fetchAlerts();

    // Set up polling
    const interval = setInterval(fetchCount, POLLING_INTERVAL);

    return () => clearInterval(interval);
  }, [fetchAlerts, fetchCount]);

  // Open modal and fetch full alerts
  const handleOpenModal = async () => {
    setIsModalVisible(true);
    setIsLoading(true);
    await fetchAlerts();
    setIsLoading(false);
  };

  // Mark alert as read
  const handleMarkAsRead = async (alert: Alert) => {
    if (alert.read) return;

    try {
      await alertsApi.markAsRead(alert.id);
      setAlerts((prev) =>
        prev.map((a) => (a.id === alert.id ? { ...a, read: true } : a))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('Error marking alert as read:', error);
    }
  };

  // Dismiss alert
  const handleDismiss = async (alertId: number) => {
    try {
      await alertsApi.dismiss(alertId);
      const dismissedAlert = alerts.find((a) => a.id === alertId);
      setAlerts((prev) => prev.filter((a) => a.id !== alertId));
      if (dismissedAlert && !dismissedAlert.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      logger.error('Error dismissing alert:', error);
    }
  };

  // Mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      await alertsApi.markAllAsRead();
      setAlerts((prev) => prev.map((a) => ({ ...a, read: true })));
      setUnreadCount(0);
    } catch (error) {
      logger.error('Error marking all as read:', error);
    }
  };

  // Handle alert action
  const handleAlertAction = (alert: Alert) => {
    handleMarkAsRead(alert);
    setIsModalVisible(false);

    if (onAlertPress) {
      onAlertPress(alert);
    } else if (alert.action_url) {
      // Navigate based on action_url
      const route = alert.action_url.replace('/', '');
      if (route === 'dashboard') {
        navigation.navigate('Dashboard' as never);
      } else if (route === 'chat') {
        navigation.navigate('Chats' as never);
      } else if (route === 'goals') {
        navigation.navigate('Goals' as never);
      }
    }
  };

  // Get severity icon
  const getSeverityIcon = (severity: string) => {
    switch (severity) {
      case 'success':
        return 'check-circle';
      case 'warning':
        return 'alert';
      case 'error':
        return 'alert-circle';
      default:
        return 'information';
    }
  };

  // Render individual alert
  const renderAlert = (alert: Alert) => (
    <TouchableOpacity
      key={alert.id}
      style={[styles.alertItem, !alert.read && styles.alertItemUnread]}
      onPress={() => handleAlertAction(alert)}
      activeOpacity={0.7}
    >
      <View style={[styles.alertIconContainer, { backgroundColor: `${alert.color}20` }]}>
        <Icon name={alert.icon} size={24} color={alert.color} />
      </View>

      <View style={styles.alertContent}>
        <View style={styles.alertHeader}>
          <Text style={styles.alertTitle} numberOfLines={1}>
            {alert.title}
          </Text>
          <TouchableOpacity
            style={styles.dismissButton}
            onPress={() => handleDismiss(alert.id)}
            hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          >
            <Icon name="close" size={16} color={colors.textSecondary} />
          </TouchableOpacity>
        </View>

        {alert.message && (
          <Text style={styles.alertMessage} numberOfLines={2}>
            {alert.message}
          </Text>
        )}

        <View style={styles.alertFooter}>
          <Text style={styles.alertTime}>
            {formatTimeAgo(alert.created_at)}
          </Text>
          {alert.action_label && (
            <Text style={[styles.alertAction, { color: alert.color }]}>
              {alert.action_label}
            </Text>
          )}
        </View>
      </View>

      {!alert.read && <View style={styles.unreadDot} />}
    </TouchableOpacity>
  );

  return (
    <>
      {/* Bell Button */}
      <Animated.View style={{ transform: [{ scale: pulseAnim }] }}>
        <TouchableOpacity
          style={styles.bellButton}
          onPress={handleOpenModal}
          activeOpacity={0.7}
        >
          <Icon name="bell-outline" size={24} color={colors.text} />
          {unreadCount > 0 && (
            <View style={styles.badge}>
              <Text style={styles.badgeText}>
                {unreadCount > 9 ? '9+' : unreadCount}
              </Text>
            </View>
          )}
        </TouchableOpacity>
      </Animated.View>

      {/* Alerts Modal */}
      <Modal
        visible={isModalVisible}
        animationType="slide"
        presentationStyle="pageSheet"
        onRequestClose={() => setIsModalVisible(false)}
      >
        <View style={styles.modalContainer}>
          {/* Header */}
          <View style={styles.modalHeader}>
            <Text style={styles.modalTitle}>Notifications</Text>
            <View style={styles.modalActions}>
              {unreadCount > 0 && (
                <TouchableOpacity
                  style={styles.markAllButton}
                  onPress={handleMarkAllAsRead}
                >
                  <Text style={styles.markAllText}>Tout marquer lu</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setIsModalVisible(false)}
              >
                <Icon name="close" size={24} color={colors.text} />
              </TouchableOpacity>
            </View>
          </View>

          {/* Alerts List */}
          <ScrollView
            style={styles.alertsList}
            contentContainerStyle={styles.alertsListContent}
            showsVerticalScrollIndicator={false}
          >
            {isLoading ? (
              <View style={styles.loadingContainer}>
                <Text style={styles.loadingText}>Chargement...</Text>
              </View>
            ) : alerts.length === 0 ? (
              <View style={styles.emptyContainer}>
                <Icon
                  name="bell-off-outline"
                  size={48}
                  color={colors.textSecondary}
                />
                <Text style={styles.emptyTitle}>Aucune notification</Text>
                <Text style={styles.emptySubtitle}>
                  Tu recevras ici tes alertes et conseils personnalises
                </Text>
              </View>
            ) : (
              alerts.map(renderAlert)
            )}
          </ScrollView>
        </View>
      </Modal>
    </>
  );
}

// Helper function to format time ago
function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) {
    return "A l'instant";
  } else if (diffMins < 60) {
    return `Il y a ${diffMins} min`;
  } else if (diffHours < 24) {
    return `Il y a ${diffHours}h`;
  } else if (diffDays === 1) {
    return 'Hier';
  } else if (diffDays < 7) {
    return `Il y a ${diffDays} jours`;
  } else {
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
    });
  }
}

const styles = StyleSheet.create({
  // Bell Button
  bellButton: {
    padding: spacing.sm,
    position: 'relative',
  },
  badge: {
    position: 'absolute',
    top: 4,
    right: 4,
    backgroundColor: colors.error,
    borderRadius: 10,
    minWidth: 18,
    height: 18,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 4,
  },
  badgeText: {
    color: '#FFFFFF',
    fontSize: 10,
    fontWeight: typography.fontWeight.bold,
  },

  // Modal
  modalContainer: {
    flex: 1,
    backgroundColor: colors.background,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: spacing.lg,
    paddingTop: Platform.OS === 'ios' ? 60 : 20,
    paddingBottom: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  modalTitle: {
    fontSize: typography.fontSize.xl,
    fontWeight: typography.fontWeight.bold,
    color: colors.text,
  },
  modalActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  markAllButton: {
    marginRight: spacing.md,
  },
  markAllText: {
    fontSize: typography.fontSize.sm,
    color: colors.primary,
    fontWeight: typography.fontWeight.medium,
  },
  closeButton: {
    padding: spacing.xs,
  },

  // Alerts List
  alertsList: {
    flex: 1,
  },
  alertsListContent: {
    paddingVertical: spacing.sm,
  },

  // Alert Item
  alertItem: {
    flexDirection: 'row',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    backgroundColor: colors.background,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  alertItemUnread: {
    backgroundColor: `${colors.primary}08`,
  },
  alertIconContainer: {
    width: 44,
    height: 44,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: spacing.md,
  },
  alertContent: {
    flex: 1,
  },
  alertHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
  },
  alertTitle: {
    flex: 1,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginRight: spacing.sm,
  },
  dismissButton: {
    padding: 2,
  },
  alertMessage: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    lineHeight: 20,
    marginBottom: spacing.xs,
  },
  alertFooter: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  alertTime: {
    fontSize: typography.fontSize.xs,
    color: colors.textSecondary,
  },
  alertAction: {
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.medium,
  },
  unreadDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.primary,
    marginLeft: spacing.sm,
    alignSelf: 'center',
  },

  // Empty State
  emptyContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xxl * 2,
    paddingHorizontal: spacing.xl,
  },
  emptyTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: typography.fontWeight.semibold,
    color: colors.text,
    marginTop: spacing.md,
    marginBottom: spacing.xs,
  },
  emptySubtitle: {
    fontSize: typography.fontSize.sm,
    color: colors.textSecondary,
    textAlign: 'center',
    lineHeight: 20,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: spacing.xxl * 2,
  },
  loadingText: {
    fontSize: typography.fontSize.md,
    color: colors.textSecondary,
  },
});
